import { useTranslation } from 'react-i18next';
import type { BbrAttributes } from '$lib/api/types';
import { formatRooms, formatSqm, formatYear } from '$lib/lib/formatters';

interface FactProps {
  label: string;
  value: React.ReactNode;
  numeric?: boolean;
  testId?: string;
}

function Fact({ label, value, numeric, testId }: FactProps) {
  return (
    <div data-testid={testId} className="flex flex-col gap-0.5 border-r border-rule px-3 py-2 last:border-r-0">
      <span className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</span>
      <span className={numeric ? 'num text-sm text-fg' : 'text-sm text-fg'}>{value ?? '—'}</span>
    </div>
  );
}

export interface BbrAttributesPanelProps {
  bbr: BbrAttributes;
}

const HEATING_KEYS: Record<string, string> = {
  district: 'bbr.heatingDistrict',
  gas: 'bbr.heatingGas',
  electric: 'bbr.heatingElectric',
  other: 'bbr.heatingOther'
};

const TYPE_KEYS: Record<string, string> = {
  apartment: 'bbr.typeApartment',
  townhouse: 'bbr.typeTownhouse',
  detached: 'bbr.typeDetached',
  'semi-detached': 'bbr.typeSemiDetached',
  Etagebolig: 'bbr.typeApartment',
  Villa: 'bbr.typeVilla',
  Parcelhus: 'bbr.typeDetached',
  Rækkehus: 'bbr.typeTownhouse',
  Dobbelthus: 'bbr.typeSemiDetached',
  Tofamiliehus: 'bbr.typeTwoFamily',
  Kollegium: 'bbr.typeDormitory'
};

export function BbrAttributesPanel({ bbr }: BbrAttributesPanelProps) {
  const { t } = useTranslation();
  const features = [
    bbr.hasBalcony ? t('bbr.balcony') : null,
    bbr.hasElevator ? t('bbr.elevator') : null
  ].filter(Boolean);
  return (
    <section
      data-testid="bbr-panel"
      aria-label={t('bbr.panelAriaLabel')}
      className="grid grid-cols-2 overflow-hidden rounded-sm border border-rule bg-surface md:grid-cols-4 xl:grid-cols-7"
    >
      <Fact label={t('bbr.m2')} value={formatSqm(bbr.m2)} numeric testId="bbr-m2" />
      <Fact label={t('bbr.rooms')} value={formatRooms(bbr.rooms)} numeric />
      <Fact label={t('bbr.yearBuilt')} value={formatYear(bbr.yearBuilt)} numeric />
      <Fact
        label={t('bbr.renovated')}
        value={bbr.yearRenovated ? formatYear(bbr.yearRenovated) : '—'}
        numeric
      />
      <Fact label={t('bbr.type')} value={bbr.buildingType ? t(TYPE_KEYS[bbr.buildingType] ?? bbr.buildingType) : '—'} />
      <Fact label={t('bbr.energy')} value={bbr.energyRating ?? '—'} />
      <Fact label={t('bbr.heating')} value={bbr.heating ? t(HEATING_KEYS[bbr.heating]) : '—'} />
      {features.length > 0 && (
        <div className="col-span-2 flex flex-wrap items-center gap-2 border-t border-rule px-3 py-2 md:col-span-4 xl:col-span-7">
          <span className="text-2xs uppercase tracking-wider text-fg-subtle">{t('bbr.features')}</span>
          {features.map((f) => (
            <span key={f as string} className="rounded-xs border border-rule bg-surface-2 px-1.5 py-0.5 text-2xs text-fg">
              {f}
            </span>
          ))}
        </div>
      )}
      <div className="col-span-2 flex items-center gap-3 border-t border-rule px-3 py-1.5 text-2xs text-fg-subtle md:col-span-4 xl:col-span-7">
        <span>{t('bbr.bbrId')} <span className="num text-fg-muted">{bbr.bbrId ?? '—'}</span></span>
        <span>·</span>
        <span>{t('bbr.building')} <span className="num text-fg-muted">{bbr.buildingId ?? '—'}</span></span>
        <span>·</span>
        <span>
          {t('bbr.resolution')}{' '}
          <span className="num text-fg-muted">{Math.round(bbr.resolutionConfidence * 100)}%</span>
        </span>
      </div>
    </section>
  );
}
